import { buildPrincipal } from "./permissions.ts";
import type { StaffOpsState } from "./types.ts";

const now = "2026-06-06T14:30:00.000Z";

export function createInitialStaffOpsState(): StaffOpsState {
  const zones = [
    {
      id: "zone-main",
      branchId: "branch-thonglor",
      name: "Main Floor",
      nameTh: "โซนหลัก",
      sortOrder: 1,
    },
    {
      id: "zone-terrace",
      branchId: "branch-thonglor",
      name: "Terrace",
      nameTh: "ระเบียง",
      sortOrder: 2,
    },
    { id: "zone-bar", branchId: "branch-thonglor", name: "Bar", nameTh: "บาร์", sortOrder: 3 },
  ];

  const tables = Array.from({ length: 16 }, (_, index) => {
    const tableNo = index + 1;
    const zone = zones[index < 7 ? 0 : index < 12 ? 1 : 2]!;
    const statuses = ["occupied", "free", "pending", "disabled"] as const;
    const status = statuses[index % statuses.length]!;
    return {
      id: `ts-${String(tableNo).padStart(2, "0")}`,
      tableId: `tbl-${String(tableNo).padStart(2, "0")}`,
      zoneId: zone.id,
      label: zone.id === "zone-bar" ? `Bar Seat ${tableNo - 12}` : `Table ${tableNo}`,
      code: zone.id === "zone-bar" ? `B${tableNo - 12}` : `T${String(tableNo).padStart(2, "0")}`,
      status,
      capacity: zone.id === "zone-bar" ? 1 : tableNo % 3 === 0 ? 6 : 4,
      partySize: status === "occupied" ? (tableNo % 4) + 1 : 0,
      socialActive: status === "occupied" && tableNo % 2 === 0,
      orderOnly: tableNo % 5 === 0,
      seatedAt: status === "occupied" ? `20:${String(10 + tableNo).padStart(2, "0")}` : undefined,
    };
  });

  const audit = (
    id: string,
    action: string,
    entityType: string,
    entityId: string,
    after: string,
  ) => ({
    id,
    actor: "Mali Floor",
    action,
    entityType,
    entityId,
    after,
    createdAt: now,
  });

  return {
    venue: { id: "venue-smoke", name: "Smoke & Mirrors", nameTh: "สโมค แอนด์ มิเรอร์" },
    branch: { id: "branch-thonglor", venueId: "venue-smoke", name: "Thonglor", nameTh: "ทองหล่อ" },
    zones,
    session: {
      id: "vs-2026-06-06",
      branchId: "branch-thonglor",
      label: "Saturday soft opening · คืนวันเสาร์",
      status: "scheduled",
      socialPaused: false,
      expectedCloseAt: "2026-06-06T18:00:00.000Z",
    },
    staffUsers: [
      buildPrincipal({
        id: "staff-waiter",
        displayName: "Dao Waiter",
        staffCode: "DAO",
        branchId: "branch-thonglor",
        roles: ["waiter"],
      }),
      buildPrincipal({
        id: "staff-bar",
        displayName: "Ben Bar",
        staffCode: "BAR",
        branchId: "branch-thonglor",
        roles: ["bartender", "runner"],
      }),
      buildPrincipal({
        id: "staff-cash",
        displayName: "Cash Narin",
        staffCode: "CASH",
        branchId: "branch-thonglor",
        roles: ["cashier"],
      }),
      buildPrincipal({
        id: "staff-mod",
        displayName: "Mod Pim",
        staffCode: "MOD",
        branchId: "branch-thonglor",
        roles: ["moderator"],
      }),
      buildPrincipal({
        id: "staff-floor",
        displayName: "Mali Floor",
        staffCode: "FLOOR",
        branchId: "branch-thonglor",
        roles: ["floor_manager"],
      }),
    ],
    tables,
    orders: [
      {
        id: "ord-pending",
        tableSessionId: "ts-01",
        tableLabel: "Table 1",
        station: "bar",
        source: "guest_order",
        status: "pending",
        lines: [
          {
            id: "ol-1",
            name: "Smoked Old Fashioned",
            nameTh: "โอลด์แฟชั่นรมควัน",
            qty: 2,
            priceMinor: 32000,
            modifiers: ["less sweet"],
          },
        ],
        totalMinor: 64000,
        note: "Guest asked for water too.",
        placedAt: "21:05",
        updatedAt: "21:05",
        timeline: [audit("aud-o1", "created", "Order", "ord-pending", "pending")],
      },
      {
        id: "ord-accepted",
        tableSessionId: "ts-02",
        tableLabel: "Table 2",
        station: "kitchen",
        source: "guest_order",
        status: "accepted",
        lines: [
          {
            id: "ol-2",
            name: "Truffle Fries",
            nameTh: "เฟรนช์ฟรายทรัฟเฟิล",
            qty: 1,
            priceMinor: 18000,
          },
        ],
        totalMinor: 18000,
        placedAt: "21:02",
        updatedAt: "21:08",
        timeline: [audit("aud-o2", "accepted", "Order", "ord-accepted", "accepted")],
      },
      {
        id: "ord-preparing",
        tableSessionId: "ts-04",
        tableLabel: "Table 4",
        station: "bar",
        source: "gift",
        giftId: "gift-accepted",
        status: "preparing",
        lines: [
          {
            id: "ol-3",
            name: "Lime Lemongrass Highball",
            nameTh: "ไฮบอลตะไคร้มะนาว",
            qty: 2,
            priceMinor: 26000,
          },
        ],
        totalMinor: 52000,
        placedAt: "20:55",
        updatedAt: "21:10",
        timeline: [audit("aud-o3", "preparing", "Order", "ord-preparing", "preparing")],
      },
      {
        id: "ord-ready",
        tableSessionId: "ts-06",
        tableLabel: "Table 6",
        station: "runner",
        source: "guest_order",
        status: "ready",
        lines: [{ id: "ol-4", name: "Singha Draft", nameTh: "สิงห์สด", qty: 3, priceMinor: 12000 }],
        totalMinor: 36000,
        placedAt: "20:47",
        updatedAt: "21:11",
        timeline: [audit("aud-o4", "ready", "Order", "ord-ready", "ready")],
      },
      {
        id: "ord-served",
        tableSessionId: "ts-08",
        tableLabel: "Table 8",
        station: "cashier",
        source: "guest_order",
        status: "served",
        lines: [{ id: "ol-5", name: "Sparkling Water", nameTh: "น้ำแร่", qty: 2, priceMinor: 9000 }],
        totalMinor: 18000,
        placedAt: "20:30",
        updatedAt: "20:46",
        timeline: [audit("aud-o5", "served", "Order", "ord-served", "served")],
      },
      {
        id: "ord-cancelled",
        tableSessionId: "ts-10",
        tableLabel: "Table 10",
        station: "bar",
        source: "guest_order",
        status: "cancelled",
        lines: [{ id: "ol-6", name: "Negroni", nameTh: "เนโกรนี", qty: 1, priceMinor: 30000 }],
        totalMinor: 30000,
        placedAt: "20:20",
        updatedAt: "20:25",
        timeline: [audit("aud-o6", "cancelled", "Order", "ord-cancelled", "cancelled")],
      },
      {
        id: "ord-rejected",
        tableSessionId: "ts-12",
        tableLabel: "Table 12",
        station: "bar",
        source: "guest_order",
        status: "rejected",
        lines: [
          { id: "ol-7", name: "Unavailable Wine", nameTh: "ไวน์หมด", qty: 1, priceMinor: 42000 },
        ],
        totalMinor: 42000,
        placedAt: "20:12",
        updatedAt: "20:13",
        timeline: [audit("aud-o7", "rejected", "Order", "ord-rejected", "rejected")],
      },
    ],
    gifts: [
      {
        id: "gift-pending",
        senderTableLabel: "Table 7",
        recipientTableLabel: "Table 12",
        status: "pending",
        items: [{ id: "gl-1", name: "Highball", nameTh: "ไฮบอล", qty: 1, priceMinor: 26000 }],
        message: "Have a nice night",
        alcohol: true,
        paymentId: "pay-g1",
      },
      {
        id: "gift-accepted",
        senderTableLabel: "Table 2",
        recipientTableLabel: "Table 4",
        status: "accepted",
        items: [
          {
            id: "gl-2",
            name: "Lemongrass Highball",
            nameTh: "ไฮบอลตะไคร้",
            qty: 2,
            priceMinor: 26000,
          },
        ],
        message: "Cheers from us",
        alcohol: true,
        paymentId: "pay-g2",
        acceptedAt: "21:02",
      },
      {
        id: "gift-rejected",
        senderTableLabel: "Table 3",
        recipientTableLabel: "Table 8",
        status: "rejected",
        items: [{ id: "gl-3", name: "Mocktail", nameTh: "ม็อกเทล", qty: 1, priceMinor: 19000 }],
        alcohol: false,
        paymentId: "pay-g3",
        refundState: "voided",
      },
      {
        id: "gift-expired",
        senderTableLabel: "Table 9",
        recipientTableLabel: "Table 1",
        status: "expired",
        items: [{ id: "gl-4", name: "Beer", nameTh: "เบียร์", qty: 1, priceMinor: 12000 }],
        alcohol: true,
        paymentId: "pay-g4",
        refundState: "voided",
      },
      {
        id: "gift-staff-rejected",
        senderTableLabel: "Table 11",
        recipientTableLabel: "Table 13",
        status: "staff_rejected",
        items: [{ id: "gl-5", name: "Shot", nameTh: "ช็อต", qty: 1, priceMinor: 15000 }],
        alcohol: true,
        paymentId: "pay-g5",
        refundState: "refund_pending",
      },
      {
        id: "gift-fulfilled",
        senderTableLabel: "Bar Seat 1",
        recipientTableLabel: "Table 6",
        status: "fulfilled",
        items: [{ id: "gl-6", name: "Sparkling Water", nameTh: "น้ำแร่", qty: 1, priceMinor: 9000 }],
        alcohol: false,
        paymentId: "pay-g6",
        fulfilledAt: "20:45",
      },
      {
        id: "gift-refunded",
        senderTableLabel: "Table 16",
        recipientTableLabel: "Table 2",
        status: "refunded",
        items: [{ id: "gl-7", name: "Wine", nameTh: "ไวน์", qty: 1, priceMinor: 42000 }],
        alcohol: true,
        paymentId: "pay-g7",
        refundState: "refunded",
      },
      {
        id: "gift-voided",
        senderTableLabel: "Table 5",
        recipientTableLabel: "Table 14",
        status: "voided",
        items: [{ id: "gl-8", name: "Beer", nameTh: "เบียร์", qty: 1, priceMinor: 12000 }],
        alcohol: true,
        paymentId: "pay-g8",
        refundState: "voided",
      },
    ],
    payments: [
      {
        id: "pay-t1",
        tableSessionId: "ts-01",
        tableLabel: "Table 1",
        status: "authorized",
        amountMinor: 128000,
        giftPaidMinor: 0,
      },
      {
        id: "pay-t2",
        tableSessionId: "ts-02",
        tableLabel: "Table 2",
        status: "captured",
        method: "promptpay",
        amountMinor: 86000,
        giftPaidMinor: 52000,
      },
      {
        id: "pay-g2",
        tableSessionId: "ts-04",
        tableLabel: "Table 4",
        status: "authorized",
        amountMinor: 52000,
        giftPaidMinor: 52000,
      },
      {
        id: "pay-r1",
        tableSessionId: "ts-11",
        tableLabel: "Table 11",
        status: "refund_pending",
        amountMinor: 15000,
        giftPaidMinor: 15000,
        refundReason: "Staff rejected social gift",
      },
      {
        id: "pay-v1",
        tableSessionId: "ts-05",
        tableLabel: "Table 5",
        status: "voided",
        amountMinor: 12000,
        giftPaidMinor: 12000,
      },
      {
        id: "pay-f1",
        tableSessionId: "ts-09",
        tableLabel: "Table 9",
        status: "failed",
        amountMinor: 30000,
        giftPaidMinor: 0,
      },
      {
        id: "pay-i1",
        tableSessionId: "ts-15",
        tableLabel: "Table 15",
        status: "initiated",
        amountMinor: 46000,
        giftPaidMinor: 0,
      },
      {
        id: "pay-ref1",
        tableSessionId: "ts-16",
        tableLabel: "Table 16",
        status: "refunded",
        amountMinor: 42000,
        giftPaidMinor: 42000,
      },
    ],
    reports: [
      {
        id: "rep-submitted",
        targetType: "gift_message",
        targetLabel: "Gift message to Table 12",
        reason: "Inappropriate message",
        snapshot: "Message hidden from recipient until staff review.",
        severity: "high",
        status: "submitted",
        submittedAt: "21:12",
        history: [audit("aud-r1", "submitted", "ModerationReport", "rep-submitted", "submitted")],
      },
      {
        id: "rep-triaged",
        targetType: "contact_request",
        targetLabel: "Contact request from Table 3",
        reason: "Repeated unwanted contact",
        snapshot: "No contact handle disclosed.",
        severity: "medium",
        status: "triaged",
        submittedAt: "20:58",
        history: [audit("aud-r2", "triaged", "ModerationReport", "rep-triaged", "triaged")],
      },
      {
        id: "rep-actioned",
        targetType: "profile",
        targetLabel: "Profile card",
        reason: "Unsafe profile text",
        snapshot: "Profile hidden from Around feed.",
        severity: "critical",
        status: "actioned",
        submittedAt: "20:40",
        history: [audit("aud-r3", "actioned", "ModerationReport", "rep-actioned", "actioned")],
      },
      {
        id: "rep-dismissed",
        targetType: "table_session",
        targetLabel: "Table 8",
        reason: "Mistaken report",
        snapshot: "No user identity disclosed.",
        severity: "low",
        status: "dismissed",
        submittedAt: "20:10",
        history: [audit("aud-r4", "dismissed", "ModerationReport", "rep-dismissed", "dismissed")],
      },
      {
        id: "rep-escalated",
        targetType: "table_session",
        targetLabel: "Table 13",
        reason: "Safety concern",
        snapshot: "Escalated to floor manager.",
        severity: "critical",
        status: "escalated",
        submittedAt: "19:55",
        history: [audit("aud-r5", "escalated", "ModerationReport", "rep-escalated", "escalated")],
      },
    ],
    incidents: [
      {
        id: "inc-1",
        reportId: "rep-escalated",
        title: "Safety escalation at terrace",
        severity: "critical",
        status: "investigating",
        createdAt: "20:00",
        summary: "Floor manager notified; users separated without exposing reporter identity.",
        notes: [
          {
            id: "aud-i1",
            author: "Mali Floor",
            note: "Escalated from moderation queue; investigating.",
            createdAt: now,
          },
        ],
      },
      {
        id: "inc-2",
        title: "Refund review",
        severity: "medium",
        status: "open",
        createdAt: "21:08",
        summary: "Review staff-rejected gift and refund state.",
        notes: [
          {
            id: "aud-i2",
            author: "Mali Floor",
            note: "Opened for refund follow-up.",
            createdAt: now,
          },
        ],
      },
    ],
    auditLog: [audit("aud-session", "scheduled", "VenueSession", "vs-2026-06-06", "scheduled")],
    analyticsEvents: [],
    connection: { degraded: false, offline: false },
  };
}
